import { Injectable } from '@angular/core';
import {
  BaseCoreState,
  baseDocumentSelectors,
  baseRelatedCustomerLocationsSelectors,
  baseDocumentActions,
} from '../../_store';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { DocumentListItemResponse } from '../../../api';
import { filter, map, take, switchMap, tap } from 'rxjs/operators';
import { DocumentCategoryType } from '../../modules/documents/_types/base-document-category.type';

@Injectable({ providedIn: 'root' })
export class BaseDocumentsService {
  constructor(private store: Store<BaseCoreState>) {}

  getDocumentsWithCategories(categoryType: DocumentCategoryType, isCustomerLocation?: boolean) {
    const documents$ = (
      isCustomerLocation
        ? this.store.select(baseRelatedCustomerLocationsSelectors.getRelatedCustomerLocationDocuments)
        : this.store.select(baseDocumentSelectors.getDocuments)
    ).pipe(
      filter((state) => !!state.ok),
      map((state) => state.data ?? [])
    );

    const categoriesOnce$ = this.getDocumentCategories$(categoryType).pipe(take(1));

    return combineLatest([documents$, categoriesOnce$]).pipe(
      map(([documents, categories]) => ({
        categories,
        documents: this.attachLabelsToDocuments(documents, categories),
      }))
    );
  }

  getDocumentCategories$(categoryType: DocumentCategoryType): Observable<{ value: number; label: string }[]> {
    return this.store.select(baseDocumentSelectors.getDocumentCategories).pipe(
      take(1),
      tap((state) => {
        if (state.ok === undefined && !state.data) {
          this.store.dispatch(baseDocumentActions.getDocumentCategories());
        }
      }),
      switchMap(() =>
        this.store.select(baseDocumentSelectors.getDocumentCategories).pipe(
          filter((state) => state.ok !== undefined),
          take(1),
          map((state) => {
            const raw = state.data?.[categoryType] as Record<number, string> | undefined;
            return this.mapCategories(raw);
          })
        )
      )
    );
  }

  mapCategories(raw?: Record<number, string> | null): { value: number; label: string }[] {
    if (!raw) {
      return [];
    }

    return Object.entries(raw).map(([key, label]) => ({ value: +key, label }));
  }

  attachLabelsToDocuments(documents: DocumentListItemResponse[], categories: { value: number; label: string }[]) {
    const categoryLabelMap = new Map<number, string>(categories.map((category) => [category.value, category.label]));
    return documents.map((document) => ({
      document,
      categoryLabel: categoryLabelMap.get(document.category),
    }));
  }
}
